import OpIcon from 'components/Common/op-ui-wrappers/OpIcon/OpIcon';
import OpText from 'components/Common/op-ui-wrappers/OpText/OpText';
import { QuantityOption, QuantityOptionsInfo } from 'gql/graphql';
import useClickOutside from 'hooks/useClickOutside';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import isDesktop from 'store/app/selector/isDesktop';
import { BasketItemSelectorProps } from '../types';
import styles from './BasketItemQuantitySelector.module.scss';

export interface BasketItemQuantitySelectorProps extends BasketItemSelectorProps {
  quantities?: QuantityOptionsInfo | null;
}

const BasketItemQuantitySelector: React.FC<BasketItemQuantitySelectorProps> = ({ quantities }) => {
  const quantityButtonRef = React.useRef<HTMLDivElement>(null);
  const optionsRef = React.useRef<HTMLDivElement>(null);

  const [selectedQuantity, setSelectedQuantity] = React.useState<QuantityOption | null>(null);
  const [optionsVisible, setOptionsVisible] = React.useState(false);
  const isDesktopDevice = useSelector(isDesktop);
  const { t } = useTranslation();

  useClickOutside(
    optionsRef,
    () => {
      setOptionsVisible(false);
    },
    quantityButtonRef,
  );

  useEffect(() => {
    if (quantities?.quantityOptions?.length) {
      const defaultQuantity = quantities?.quantityOptions?.find((q) => q?.isDefault);
      if (defaultQuantity) {
        setSelectedQuantity(defaultQuantity);
      }
    }
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.quantity}>
        <div className={styles.title}>
          <OpText variant="body" size="medium">
            {isDesktopDevice ? t('txt_quantity') : t('txt_quantity_short')}
          </OpText>
        </div>
        <div
          className={styles.quantityButton}
          ref={quantityButtonRef}
          onClick={() => setOptionsVisible(!optionsVisible)}
        >
          <OpText size="small" variant="body">
            {selectedQuantity?.quantity}
          </OpText>
          <OpIcon icon="dropdown-arrow-down" size="small" />
        </div>
      </div>
      {optionsVisible && (
        <div className={styles.options} ref={optionsRef}>
          {!isDesktopDevice && (
            <>
              <div className={styles.popupHead}>
                <div className={styles.title}>
                  <OpText variant="body" size="large">
                    {t('txt_quantity')}
                  </OpText>
                </div>
                <div className={styles.close} onClick={() => setOptionsVisible(false)}>
                  <OpIcon icon="close" size="small" />
                </div>
              </div>
              <hr />
            </>
          )}
          {quantities?.quantityOptions?.map((q) => (
            <div key={q?.quantity} className={styles.option} onClick={() => setSelectedQuantity(q)}>
              <div className={styles.quantityName}>
                <div className={styles.selectionCheckmark}>
                  {selectedQuantity?.quantity === q?.quantity && <OpIcon icon="checkmark" size="small" />}
                </div>
                <OpText variant="body" size="medium">
                  {q?.quantity} {q?.batchName}
                </OpText>
              </div>
              {q?.prices?.price && (
                <div
                  className={styles.quantityPriceInitial}
                  dangerouslySetInnerHTML={{ __html: q?.prices?.price?.formatted || '' }}
                />
              )}
              {q?.prices?.pricePerItemAccordingToVatOption && (
                <div
                  className={styles.quantityPriceDiscounted}
                  dangerouslySetInnerHTML={{ __html: q?.prices?.pricePerItemAccordingToVatOption?.formatted || '' }}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BasketItemQuantitySelector;
